import { useState } from 'react';
import { KeyValueList, PageHeader, Panel, Toolbar } from '../components/Page';
import { Chip, EntityTypeChip } from '../components/Chips';
import { CardSkeleton, EmptyState, ErrorState } from '../components/Feedback';
import { TableWrap } from '../components/Table';
import { RequireRole } from '../lib/auth';
import { GatewayRole, LEARNING_EVIDENCE_ENTITY_TYPES } from '../lib/enums';
import { learningAttachments, learningDiagnostics, learningEvidence } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatRelative } from '../lib/format';
import type { EvidenceTypeDiagnostics } from '../lib/types';

/** The freshness window the backend itself uses before an incremental sync counts as stale. */
const SYNC_FRESHNESS_HOURS = 6;

function isSyncOverdue(row: EvidenceTypeDiagnostics): boolean {
  if (!row.backfillComplete) return false;
  if (!row.lastChangesSyncUtc) return true;
  const ageMs = Date.now() - new Date(row.lastChangesSyncUtc).getTime();
  return ageMs > SYNC_FRESHNESS_HOURS * 60 * 60 * 1000;
}

function EvidenceRow({ row }: { row: EvidenceTypeDiagnostics }): JSX.Element {
  return (
    <tr>
      <td>
        <EntityTypeChip entityType={row.entityType} />
        <span className="cell-sub">{row.role}</span>
      </td>
      <td>
        {!row.backfillComplete ? (
          <Chip tone="info">Backfilling — {row.backfillNextStartPosition} rows so far</Chip>
        ) : isSyncOverdue(row) ? (
          <Chip tone="warn">Sync overdue</Chip>
        ) : (
          <Chip tone="good">Current</Chip>
        )}
      </td>
      <td>
        {row.transactionCount}
        {row.deletedCount > 0 ? <span className="cell-sub">{row.deletedCount} deleted</span> : null}
      </td>
      <td>{row.unpublishedCount}</td>
      <td>
        {row.lastChangesSyncUtc ? formatRelative(row.lastChangesSyncUtc) : 'never'}
        {row.lastMessage ? <span className="cell-sub">{row.lastMessage}</span> : null}
      </td>
    </tr>
  );
}

/**
 * COMPANY-WIDE EVIDENCE ACQUISITION STATUS ONLY.
 *
 * Turning AI Learning on and publishing it is a per-vendor decision, made on that vendor's own
 * card — see VendorLearningPanel, opened from the Vendors page's "AI Learning" row action. This
 * page is what is left once that moved: pulling/syncing QuickBooks history into the gateway and
 * discovering/retrieving attachments both happen automatically for the whole connected company
 * regardless of which vendors have learning enabled, so a per-vendor page is the wrong home for
 * them. This dashboard is where an admin checks that acquisition is healthy, or nudges it along.
 */
function AiLearningContent(): JSX.Element {
  const toast = useToast();
  const diagnostics = useAsync((signal) => learningDiagnostics.get(signal), []);

  const [busy, setBusy] = useState<string | null>(null);
  const [rescanGapFor, setRescanGapFor] = useState<string[] | null>(null);

  const d = diagnostics.data;

  const run = async <T,>(name: string, action: () => Promise<T>, onSuccess?: (result: T) => void) => {
    setBusy(name);
    try {
      const result = await action();
      onSuccess?.(result);
      return result;
    } catch (caught) {
      toast.error(`${name} did not complete`, errorMessage(caught));
      return undefined;
    } finally {
      setBusy(null);
    }
  };

  const pullNow = () =>
    run('Pull now', () => learningEvidence.pull({}), (responses) => {
      const paused = responses.filter((r) => !r.complete).length;
      toast.success(
        'Pull finished',
        paused > 0
          ? `${paused} of ${responses.length} entity types paused and can be pulled again.`
          : 'Every entity type read to the end of what QuickBooks has.',
      );
      diagnostics.reload();
    });

  const syncNow = () =>
    run('Sync now', () => learningEvidence.sync({}), () => {
      toast.success('Sync finished', 'Incremental changes since the last pass have been pulled.');
      diagnostics.reload();
    });

  const checkDeletions = () =>
    run('Check for deletions', () => learningEvidence.syncDeletions(), (outcome) => {
      setRescanGapFor(outcome.fullRescanRequired ? outcome.fullRescanRequiredFor.map(String) : null);
      toast.success(
        'Deletion check finished',
        outcome.markedDeleted > 0 ? `${outcome.markedDeleted} row(s) marked deleted.` : 'Nothing new to mark deleted.',
      );
      diagnostics.reload();
    });

  const discoverAttachments = () =>
    run('Discover attachments', () => learningAttachments.discover(), (outcome) => {
      toast.success('Discovery finished', outcome.message ?? `${outcome.created} new attachment(s) found.`);
      diagnostics.reload();
    });

  const retrieveAttachments = () =>
    run('Retrieve attachment content', () => learningAttachments.retrieve(), (outcome) => {
      toast.success(
        'Retrieval finished',
        outcome.message ?? `${outcome.retrieved} retrieved, ${outcome.deduplicated} deduplicated, ${outcome.failed} failed.`,
      );
      diagnostics.reload();
    });

  if (diagnostics.loading && !d) {
    return (
      <>
        <PageHeader title="AI Learning" />
        <CardSkeleton />
      </>
    );
  }

  if (diagnostics.error && !d) {
    const isNoConnector = diagnostics.error.includes('connector identity');
    return (
      <>
        <PageHeader title="AI Learning" />
        {isNoConnector ? (
          <EmptyState
            title="QuickBooks is not connected yet"
            description="Connect QuickBooks and pull its Chart of Accounts on the Accounts page before AI Learning has anything to work with."
            icon="🔌"
          />
        ) : (
          <ErrorState message="AI Learning status could not be read." detail={diagnostics.error} onRetry={diagnostics.reload} />
        )}
      </>
    );
  }

  if (!d) return <EmptyState title="No AI Learning status returned" />;

  return (
    <>
      <PageHeader
        title="AI Learning"
        description={`Evidence for ${d.externalCompanyName ?? d.externalCompanyId} accumulates automatically in the background, for every connected vendor. Turning learning on and publishing it is done per vendor — open a supplier on the Vendors page and use its "AI Learning" action.`}
        actions={
          <>
            <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={() => void syncNow()}>
              {busy === 'Sync now' ? 'Syncing…' : 'Sync now'}
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={() => void pullNow()}>
              {busy === 'Pull now' ? 'Pulling…' : 'Pull now'}
            </button>
          </>
        }
      />

      <Panel
        title="Evidence"
        description="Bill, expense, vendor credit, bill-payment and purchase-order history pulled from QuickBooks. Backfill and sync run automatically every few minutes; Pull now just gets ahead of the next automatic pass."
      >
        <TableWrap>
          <thead>
            <tr>
              <th scope="col">Entity type</th>
              <th scope="col">Status</th>
              <th scope="col">Transactions</th>
              <th scope="col">Not yet published</th>
              <th scope="col">Last change sync</th>
            </tr>
          </thead>
          <tbody>
            {LEARNING_EVIDENCE_ENTITY_TYPES.map((entityType) => {
              const row = d.evidence.find((e) => e.entityType === entityType);
              return row ? (
                <EvidenceRow key={entityType} row={row} />
              ) : (
                <tr key={entityType}>
                  <td>
                    <EntityTypeChip entityType={entityType} />
                  </td>
                  <td colSpan={4} className="muted">
                    Not started yet.
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TableWrap>

        <Toolbar>
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy !== null} onClick={() => void checkDeletions()}>
            {busy === 'Check for deletions' ? 'Checking…' : 'Check for deletions'}
          </button>
        </Toolbar>

        {rescanGapFor && rescanGapFor.length > 0 ? (
          <div className="banner banner-warn" role="alert">
            <div className="banner-body">
              <p className="banner-title">Deletion tracking is stale</p>
              <p className="banner-detail">
                QuickBooks only answers deletion questions for the last 30 days, and the gap for {rescanGapFor.join(', ')}{' '}
                is older than that. Only a "Pull now" that runs an entity type's backfill again from the start clears
                this — it reconciles missing rows as deleted directly, which proves more than the deletion check could
                for that stretch.
              </p>
            </div>
          </div>
        ) : null}
      </Panel>

      <Panel
        title="Attachments"
        description="Discovery finds which QuickBooks records have attachments (cheap, metadata only). Retrieval downloads the actual file bytes and is the heavier of the two — a separate, explicit step."
      >
        <KeyValueList columns={3}>
          <div className="kv">
            <dt className="kv-label">Discovered</dt>
            <dd className="kv-value">{d.attachments.discovered}</dd>
          </div>
          <div className="kv">
            <dt className="kv-label">Retrieved</dt>
            <dd className="kv-value">{d.attachments.retrieved}</dd>
          </div>
          <div className="kv">
            <dt className="kv-label">Pending retrieval</dt>
            <dd className="kv-value">{d.attachments.pendingRetrieval}</dd>
          </div>
          <div className="kv">
            <dt className="kv-label">Deduplicated</dt>
            <dd className="kv-value">{d.attachments.deduplicated}</dd>
          </div>
          <div className="kv">
            <dt className="kv-label">Deleted at source</dt>
            <dd className="kv-value">{d.attachments.deletedAtSource}</dd>
          </div>
        </KeyValueList>
        <Toolbar>
          <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={() => void discoverAttachments()}>
            {busy === 'Discover attachments' ? 'Discovering…' : 'Discover attachments'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy !== null || d.attachments.pendingRetrieval === 0}
            onClick={() => void retrieveAttachments()}
          >
            {busy === 'Retrieve attachment content'
              ? 'Retrieving…'
              : `Retrieve ${d.attachments.pendingRetrieval} pending attachment(s)`}
          </button>
        </Toolbar>
      </Panel>
    </>
  );
}

/** Admin-only, mirroring AI Settings — this is a status dashboard, not where learning is turned on. */
export function AiLearningPage(): JSX.Element {
  return (
    <RequireRole minRole={GatewayRole.Admin}>
      <AiLearningContent />
    </RequireRole>
  );
}
